import { useMemo } from 'react';
import CustomDropdownWithSearch from '../CustomDropdownWithSearch/CustomDropdownWithSearch';
import { UserData } from '../../services/userService';

interface UserPickerProps {
  value: string;                 // stored user id, or a legacy free-text name
  onChange: (id: string) => void;
  users: UserData[];
  placeholder?: string;
}

/** Single-select user picker backed by the real user list. Falls back to showing
 * the raw stored string when it doesn't match a known user id, so legacy free-text
 * values (seed data) never render blank.
 *
 * The underlying dropdown is a plain string picker (matches selection by string
 * equality), so same-named users are disambiguated with their email before being
 * turned into options/labels below — otherwise every row sharing that name would
 * render as selected, and picking any of them would always resolve to whichever
 * user happened to be last in the list. */
const UserPicker: React.FC<UserPickerProps> = ({ value, onChange, users, placeholder }) => {
  const { options, labelToId, idToLabel } = useMemo(() => {
    const nameCounts = new Map<string, number>();
    users.forEach((u) => nameCounts.set(u.name, (nameCounts.get(u.name) || 0) + 1));
    const labelOf = (u: UserData) => ((nameCounts.get(u.name) || 0) > 1 ? `${u.name} (${u.email})` : u.name);
    return {
      options: users.map(labelOf),
      labelToId: new Map(users.map((u) => [labelOf(u), u.id])),
      idToLabel: new Map(users.map((u) => [u.id, labelOf(u)])),
    };
  }, [users]);

  const displayValue = value ? [idToLabel.get(value) ?? value] : [];

  return (
    <CustomDropdownWithSearch
      value={displayValue}
      options={options}
      onChange={(selected) => onChange(selected[0] ? (labelToId.get(selected[0]) ?? selected[0]) : '')}
      placeholder={placeholder || 'Select user...'}
      singleSelect
      showTags={false}
    />
  );
};

export default UserPicker;
